/**
 * Command Palette - quick access to navigation and actions.
 *
 * Inspired by VS Code, Figma, Linear, and other modern apps.
 * Opens with Cmd+K, supports keyboard navigation.
 */
import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  useCommands,
  filterCommands,
  groupCommands,
  getGroupLabel,
  type Command,
  type CommandGroup,
} from './useCommands'

interface CommandPaletteProps {
  isOpen: boolean
  onClose: () => void
}

export function CommandPalette({ isOpen, onClose }: CommandPaletteProps) {
  const [query, setQuery] = useState('')
  const [selectedIndex, setSelectedIndex] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()

  const allCommands = useCommands()
  const filteredCommands = filterCommands(allCommands, query)
  const groupedCommands = groupCommands(filteredCommands)

  // Flatten for keyboard navigation
  const flatCommands = filteredCommands

  // Reset state when opening
  useEffect(() => {
    if (isOpen) {
      setQuery('')
      setSelectedIndex(0)
      // Focus input after render
      setTimeout(() => inputRef.current?.focus(), 0)
    }
  }, [isOpen])

  // Scroll selected item into view
  useEffect(() => {
    if (!listRef.current) return
    const selected = listRef.current.querySelector('[data-selected="true"]')
    if (selected) {
      selected.scrollIntoView({ block: 'nearest' })
    }
  }, [selectedIndex])

  // Handle keyboard navigation
  const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        setSelectedIndex(prev => Math.min(prev + 1, flatCommands.length - 1))
        break
      case 'ArrowUp':
        event.preventDefault()
        setSelectedIndex(prev => Math.max(prev - 1, 0))
        break
      case 'Enter':
        event.preventDefault()
        if (flatCommands[selectedIndex]) {
          executeCommand(flatCommands[selectedIndex])
        }
        break
      case 'Escape':
        event.preventDefault()
        onClose()
        break
    }
  }, [flatCommands, selectedIndex, onClose])

  // Execute a command
  const executeCommand = useCallback((command: Command) => {
    onClose()
    command.onSelect()
  }, [onClose])

  // Check if query looks like a stock symbol (for symbol search)
  const isSymbolQuery = query.length >= 1 &&
    query.length <= 5 &&
    /^[A-Za-z]+$/.test(query) &&
    filteredCommands.length === 0

  // Handle symbol search
  const handleSymbolSearch = useCallback(() => {
    onClose()
    navigate(`/research/${encodeURIComponent(query.toUpperCase())}`)
  }, [query, navigate, onClose])

  if (!isOpen) return null

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-50"
        onClick={onClose}
      />

      {/* Palette */}
      <div className="fixed left-1/2 top-1/4 -translate-x-1/2 w-full max-w-lg z-50">
        <div className="bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden">
          {/* Search input */}
          <div className="flex items-center px-4 py-3 border-b border-gray-200">
            <SearchIcon className="h-5 w-5 text-gray-400 mr-3" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value)
                setSelectedIndex(0)
              }}
              onKeyDown={handleKeyDown}
              placeholder="Search commands or type a symbol..."
              className="flex-1 outline-none text-gray-900 placeholder-gray-400"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
            />
            <kbd className="hidden sm:inline-flex items-center px-2 py-1 text-xs text-gray-400 bg-gray-100 rounded">
              esc
            </kbd>
          </div>

          {/* Results */}
          <div ref={listRef} className="max-h-80 overflow-y-auto">
            {flatCommands.length === 0 && !isSymbolQuery ? (
              <div className="px-4 py-8 text-center text-gray-500">
                No commands found
              </div>
            ) : (
              <>
                {/* Symbol search suggestion */}
                {isSymbolQuery && (
                  <div className="px-3 pt-3">
                    <button
                      onClick={handleSymbolSearch}
                      className="w-full flex items-center px-3 py-2 rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors"
                    >
                      <SearchIcon className="h-4 w-4 mr-2" />
                      <span>Research <strong>{query.toUpperCase()}</strong></span>
                    </button>
                  </div>
                )}

                {/* Grouped commands */}
                {(['navigation', 'actions'] as CommandGroup[]).map((group) => {
                  const commands = groupedCommands[group]
                  if (commands.length === 0) return null

                  return (
                    <div key={group} className="px-3 py-2">
                      <div className="px-2 py-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        {getGroupLabel(group)}
                      </div>
                      <div className="space-y-1">
                        {commands.map((command) => {
                          const globalIndex = flatCommands.indexOf(command)
                          const isSelected = globalIndex === selectedIndex

                          return (
                            <button
                              key={command.id}
                              data-selected={isSelected}
                              onClick={() => executeCommand(command)}
                              onMouseEnter={() => setSelectedIndex(globalIndex)}
                              className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-left transition-colors ${
                                isSelected
                                  ? 'bg-blue-50 text-blue-900'
                                  : 'text-gray-700 hover:bg-gray-50'
                              }`}
                            >
                              <div className="flex items-center min-w-0">
                                <CommandIcon group={group} className="h-4 w-4 mr-3 text-gray-400 flex-shrink-0" />
                                <div className="min-w-0">
                                  <div className="font-medium truncate">{command.label}</div>
                                  {command.description && (
                                    <div className="text-sm text-gray-500 truncate">
                                      {command.description}
                                    </div>
                                  )}
                                </div>
                              </div>
                              {command.shortcut && (
                                <kbd className="ml-3 flex-shrink-0 px-2 py-1 text-xs text-gray-400 bg-gray-100 rounded font-mono">
                                  {command.shortcut}
                                </kbd>
                              )}
                            </button>
                          )
                        })}
                      </div>
                    </div>
                  )
                })}
              </>
            )}
          </div>

          {/* Footer */}
          <div className="px-4 py-2 border-t border-gray-200 bg-gray-50 text-xs text-gray-500 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-white border rounded text-gray-400">↑↓</kbd>
                navigate
              </span>
              <span className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-white border rounded text-gray-400">↵</kbd>
                select
              </span>
            </div>
            <div className="flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 bg-white border rounded text-gray-400">g</kbd>
              <span>+ key for quick nav</span>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

// Icons
function SearchIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
    </svg>
  )
}

function CommandIcon({ group, className }: { group: CommandGroup; className?: string }) {
  if (group === 'navigation') {
    return (
      <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
      </svg>
    )
  }
  if (group === 'actions') {
    return (
      <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    )
  }
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
    </svg>
  )
}
